import React, { useState } from "react";
import axios from "axios";
import { useQuery } from "react-query";
import Spinner from "components/spinner";
import Field from "components/field";
import { RiEdit2Line, RiSave3Line } from "react-icons/ri";
import { useTranslation } from "react-i18next";

type Props = {
  id?: string;
  onEdit: (id: string, updatedTerm: { text?: string; active?: boolean, type?: string, order: number }) => void;
  onLoad: (terms: any[]) => void;
  onEditStatusChange: (isEditing: boolean) => void;
};

const AdditionalTerms = ({ id, onEdit, onLoad, onEditStatusChange }: Props) => {
  const { t } = useTranslation();
  const tBase = "views.estimate.AdditionalTerms";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [showTerms, setShowTerms] = useState(false);
  const [editMode, setEditMode] = useState<Record<string, boolean>>({});
  const [editedTerms, setEditedTerms] = useState<Record<string, { text: string; active: boolean, type: string, order: number, source: string }>>({})

  const { data, isLoading } = useQuery(
    ["additionalTerms", id],
    async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/custom-doc/additional-terms/${id}`
      );
      return data;
    },
    {
      enabled: !!id,
      onSuccess: (data) => {
        const updatedTerms = data.reduce(
          (acc: Record<string, { text: string; active: boolean, type: string, order: number, source: string }>, term: any, i: number) => {
            acc[term.id] = {
              text: editedTerms[term.id]?.text ?? term.text,
              active: editedTerms[term.id]?.active ?? term.active ?? true,
              type: editedTerms[term.id]?.type ?? term.type,
              order: editedTerms[term.id]?.order ?? term.order ?? i,
              source: term.source ?? "additional",
            };
            return acc;
          },
          {}
        );
        setEditedTerms(updatedTerms);
        onLoad(data);
      },      
    }
  );

  const moveTerm = (index: number, direction: "up" | "down") => {
    if (!data) return; // just a safety check if data is undefined
  
    // First, get the sorted array to know which term is at 'index'
    const sortedTerms = data.slice().sort((a: any, b: any) => {
      const aOrder = editedTerms[a.id]?.order ?? 0;
      const bOrder = editedTerms[b.id]?.order ?? 0;
      return aOrder - bOrder;
    });
  
    const currentTerm = sortedTerms[index];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    const swapTerm = sortedTerms[newIndex];
  
    // Swap their 'order' values in our local editedTerms state
    setEditedTerms((prev) => {
      // Clone the object to ensure immutability
      const updated = { ...prev };
  
      const currentOrder = updated[currentTerm.id].order;
      const neighborOrder = updated[swapTerm.id].order;
  
      updated[currentTerm.id] = {
        ...updated[currentTerm.id],
        order: neighborOrder,
      };
      updated[swapTerm.id] = {
        ...updated[swapTerm.id],
        order: currentOrder,
      };
  
      return updated;
    });
  
    // Optionally, notify the parent of changes for both items
    // so the parent can keep track of the new order, if needed:
    onEdit(currentTerm.id, {
      ...editedTerms[currentTerm.id],
      order: editedTerms[swapTerm.id].order,
    });
    onEdit(swapTerm.id, {
      ...editedTerms[swapTerm.id],
      order: editedTerms[currentTerm.id].order,
    });
  };
  
  const handleEditToggle = (termId: string) => {
    const newEditMode = { ...editMode, [termId]: !editMode[termId] };
    setEditMode(newEditMode);

    // Notify parent about the overall editing status
    onEditStatusChange(Object.values(newEditMode).some((isEditing) => isEditing));

    if (!newEditMode[termId]) {
      onEdit(termId, { ...editedTerms[termId] });
    }
  };

  const handleTextChange = (termId: string, newText: string) => {
    setEditedTerms((prev) => {
      const updatedTerm = { ...prev[termId], text: newText };
      onEdit(termId, updatedTerm); // Sync changes with parent
      return { ...prev, [termId]: updatedTerm };
    });
  };

  const handleCheckboxChange = (termId: string) => {
    setEditedTerms((prev) => {
      const updatedTerm = {
        ...prev[termId],
        active: !prev[termId]?.active, // Toggle active state
      };
      onEdit(termId, updatedTerm); // Sync changes with parent
      return { ...prev, [termId]: updatedTerm };
    });
  };

  const handleTypeChange = (termId: string, newType: string) => {
    setEditedTerms((prev) => {
      const updatedTerm = { ...prev[termId], type: newType };
      onEdit(termId, updatedTerm); // Sync changes with parent
      return { ...prev, [termId]: updatedTerm };
    });
  }

  const sortedData = React.useMemo(() => {
    if (!data) return [];
    // Sort using the order in editedTerms
    return data.slice().sort((a: any, b: any) => {
      const aOrder = editedTerms[a.id]?.order ?? 0;
      const bOrder = editedTerms[b.id]?.order ?? 0;
      return aOrder - bOrder;
    });
  }, [data, editedTerms]);

  const additionalTermTypes = [
    { value: "none", label: tr("None") },
    { value: "yes_no", label: tr("Yes / No") },
    { value: "initials", label: tr("Initials") },
    { value: "yes_no_initials", label: tr("Yes / No + Initials") },
  ];

  return (
    <div className="flex flex-col border-b border-gray-300 pb-5">
      <button
        className="self-start text-secondary hover:underline"
        onClick={() => setShowTerms((prev) => !prev)}
      >
        {showTerms ? tr("Hide Terms and Conditions") : tr("View Terms and Conditions")}
      </button>

      {showTerms && (
        <div className="space-y-4 mt-4">
          {isLoading ? (
            <Spinner className="justify-self-start" />
          ) : (
            <>
              {data?.length > 0 ? (
                sortedData.map((term: any, i: number) => (
                  <div key={term.id} className="flex items-center space-x-2">
                    {/* <div className="flex flex-col space-y-2 text-secondary pl-0.5 pb-0.5">
                      <button 
                        onClick={() => moveTerm(i, "up")}
                        className={`
                          ${i === 0 
                            ? "text-gray-400 cursor-default" 
                            : "text-secondary cursor-pointer hover:scale-[1.2]"
                          }
                        `}
                        disabled={i === 0}
                      >
                        <FaArrowCircleUp />
                      </button>
                      <button 
                        onClick={() => moveTerm(i, "down")}
                        className={`
                          ${i === sortedData.length - 1 
                            ? "text-gray-400 cursor-default" 
                            : "text-secondary cursor-pointer hover:scale-[1.2]"
                          }
                        `}
                        disabled={i === sortedData.length - 1}
                      >
                        <FaArrowCircleDown />
                      </button>
                    </div> */}
                    <div className="space-y-2 flex-1">
                      <div className="flex justify-between">
                        <div className="flex items-center">
                          <div className="text-[12.8px]">{term.label}</div>
                            <button
                            className="ml-2 rounded-full p-1 bg-gray-100 hover:bg-gray-200"
                            onClick={() => handleEditToggle(term.id)}
                          >
                            {editMode[term.id] ? (
                              <RiSave3Line className="text-secondary" size={18} />
                            ) : (
                              <RiEdit2Line className="text-primary" size={18} />
                            )}
                          </button>
                        </div>
                        <div className="flex items-center space-x-1.5">
                          {/* Type */}
                          {editedTerms[term.id]?.source === "additional" && (
                            <div>
                              <select
                                value={editedTerms[term.id]?.type || tr("none")}
                                onChange={(e) => handleTypeChange(term.id, e.target.value)}
                                className="text-xs"
                              >
                                {additionalTermTypes.map((type) => (
                                  <option key={type.value} value={type.value} className="text-right">
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          {/* Active status */}
                          <div className="flex items-center space-x-1.5">
                            <span className="text-xs">{tr("Active")}</span>
                            <input
                              type="checkbox"
                              checked={editedTerms[term.id]?.active}
                              onChange={() => handleCheckboxChange(term.id)}
                              className="w-4 h-4"
                            />
                          </div>
                        </div>
                      </div>

                      <Field
                        disabled={!editMode[term.id]}
                        value={editedTerms[term.id]?.text}
                        type="textarea"
                        onChange={(e) =>
                          handleTextChange(term.id, e.target.value)
                        }
                        className={"!text-xs"}
                      />
                    </div>
                  </div>
                ))
              ) : (
                id ? (
                  <span className="text-gray-500">{tr("No terms available")}</span>
                ) : (
                  <span className="text-gray-500 italic">{tr("Please select a custom document template")}</span>
                )

              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdditionalTerms;
